// Development-only console access to the app's global state.
//
// Each extracted block registers a named snapshot getter, and they all show up
// under one namespace in the browser console. The surface therefore grows with
// the migration instead of being rebuilt for every block.
//
// Read-only by design: getters return a snapshot, so nothing here can drive the
// app or fire requests.

const NAMESPACE = "__funooni";

// Names the registry itself owns, so a block cannot shadow them.
const RESERVED = ["help", "state"];

// CRA replaces process.env.NODE_ENV with a literal at build time, so in a
// production build this is `false` and every branch guarded by it is dropped by
// the minifier - the namespace never reaches shipped code.
export const isDevMode = () => process.env.NODE_ENV === "development";

const noop = () => {};

const createRegistry = () => {
  const getters = new Map();

  const registry = {
    help() {
      const names = [...getters.keys()];

      if (names.length === 0) {
        console.log(`${NAMESPACE}: nothing registered yet.`);
        return;
      }

      console.log(
        [
          `${NAMESPACE} - read-only state inspection (development only)`,
          "",
          `  ${NAMESPACE}.state()   every registered block at once`,
          ...names.map((name) => `  ${NAMESPACE}.${name}()`),
        ].join("\n"),
      );
    },

    state() {
      const snapshot = {};
      getters.forEach((getSnapshot, name) => {
        snapshot[name] = getSnapshot();
      });
      return snapshot;
    },
  };

  // Kept off the registry object itself so a block named "getters" can't
  // collide with it.
  Object.defineProperty(registry, "__getters", {
    value: getters,
    enumerable: false,
  });

  return registry;
};

const ensureRegistry = () => {
  if (!window[NAMESPACE]) window[NAMESPACE] = createRegistry();
  return window[NAMESPACE];
};

/**
 * Expose a piece of global state on the dev console.
 *
 * Call it once on mount with a getter that reads the latest values - typically
 * from a ref - rather than re-registering whenever the state changes.
 *
 * @param {string} name key under the console namespace, e.g. "notificationPreferences"
 * @param {() => unknown} getSnapshot returns the current state, called on demand
 * @returns {() => void} unregister; a no-op outside development
 *
 * @example
 *   const snapshotRef = useRef();
 *   snapshotRef.current = { newsSubscription, toggling };
 *   useEffect(() => registerDevState("notificationPreferences", () => snapshotRef.current), []);
 */
export const registerDevState = (name, getSnapshot) => {
  if (!isDevMode() || typeof window === "undefined") return noop;

  if (RESERVED.includes(name)) {
    console.warn(
      `${NAMESPACE}: "${name}" is reserved by the registry - pick another name.`,
    );
    return noop;
  }

  const registry = ensureRegistry();
  registry.__getters.set(name, getSnapshot);
  registry[name] = () => getSnapshot();

  return () => {
    registry.__getters.delete(name);
    delete registry[name];
  };
};
